import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CiShop } from "react-icons/ci";
import { CgStack } from "react-icons/cg";
import { FiImage } from "react-icons/fi";
import { FaImages, FaLink, FaWpforms } from "react-icons/fa";
import { GrDiamond } from "react-icons/gr";
import { RiCodeFill } from "react-icons/ri";
import { MdLabelOutline } from "react-icons/md";
import { AiOutlineIdcard } from "react-icons/ai";
import { FaPlus, FaUsersGear } from "react-icons/fa6";
import { RxCountdownTimer, RxText } from "react-icons/rx";
import { LuMapPin, LuUserRoundMinus } from "react-icons/lu";
import { PiMusicNotesBold, PiNoteBold } from "react-icons/pi";
import { BsFilePost, BsGrid3X3GapFill, BsTelephone } from "react-icons/bs";
import { IoGridSharp, IoText, IoChatbubblesOutline } from "react-icons/io5";
import SwitchToggle from "../Common/SwitchToggle";
import { useLoader } from "../../context/LoaderContext";
import axiosUserInstance from "../../axiosUserInstance";
import MobileDropdownModal from "../Common/MobileDropdownModal";
import Gmail from "../../Assets/svg/gmail.svg";
import YouTube from "../../Assets/svg/youtube.svg";
import ImagePhoto from "../../Assets/svg/image-photo.svg";
import MapMarkerIcon from "../../Assets/svg/map-map-marker.png";

const WidgetMenu = ({
  userData,
  setUserData,
  openAddSocial,
  updateUserData,
  openWidgetModal,
  setOpenWidgetModal,
  setShowCountdownModal,
  setShowAlbumEmbedModal,
  setAlbumEmbedFormData,
  openAddForm,
}) => {
  const { setLoading } = useLoader();
  const planLimits = { free: 5, monthly: 300, yearly: 600 }; // seconds: 5s, 5min (300s), 10min (600s)
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const analyserRef = useRef(null);
  const maxSec = planLimits[userData?.planType || "free"];

  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isActive, setIsActive] = useState(false); // on-demand activation
  const [progress, setProgress] = useState(0);

  // Idle/Static visualizer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let ticker = 0;
    let anim = null;

    const drawStatic = () => {
      const w = canvas.width;
      const h = canvas.height;
      if (w === 0 || h === 0) return; // Don't draw if canvas not sized

      ctx.clearRect(0, 0, w, h);
      const barCount = 20;
      const gap = 2;
      const barWidth = (w - (barCount - 1) * gap) / barCount;
      for (let i = 0; i < barCount; i++) {
        const amp = 0.12 + 0.18 * Math.abs(Math.sin(i * 0.12));
        const barH = amp * h;
        ctx.fillStyle = "rgba(0,0,0,0.85)";
        const x = i * (barWidth + gap);
        ctx.fillRect(x, h - barH, barWidth, barH);
      }
    };

    const drawAnimated = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const barCount = 20;
      const gap = 2;
      const barWidth = (w - (barCount - 1) * gap) / barCount;
      for (let i = 0; i < barCount; i++) {
        const amp = 0.12 + 0.18 * Math.abs(Math.sin((ticker + i) * 0.12));
        const barH = amp * h;
        ctx.fillStyle = "rgba(0,0,0,0.85)";
        const x = i * (barWidth + gap);
        ctx.fillRect(x, h - barH, barWidth, barH);
      }
      ticker += 1;
      anim = requestAnimationFrame(drawAnimated);
    };

    // Small delay to ensure canvas is sized
    setTimeout(() => {
      if (!isPlaying) {
        drawStatic();
      } else if (!analyserRef.current) {
        drawAnimated();
      }
    }, 100);

    return () => {
      if (anim) cancelAnimationFrame(anim);
    };
  }, [isPlaying]);

  useEffect(() => {
    if (!userData?.audioUrl && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
  }, [userData?.audioUrl]);

  // Hook to connect Web Audio analyser and draw bars when playing
  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const stopAnalyser = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };

    if (isPlaying && isActive) {
      audio.play().catch((e) => console.warn("play prevented", e));
    } else {
      audio.pause();
    }

    return () => stopAnalyser();
  }, [isPlaying, isActive, userData?.audioUrl]);

  // Resize canvas to device pixel ratio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
    };
    setSize();
    window.addEventListener("resize", setSize);
    return () => window.removeEventListener("resize", setSize);
  }, []);

  // File input handler: send video to backend to extract audio
  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/") && !file.type.startsWith("audio/")) {
      toast.error("Please upload a video or audio file");
      return;
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("planType", userData?.planType || "free");

    try {
      setLoading(true);
      const res = await axiosUserInstance.post("/file/extract-audio", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const j = res.data;
      if (j.duration > maxSec) {
        toast.info(
          `Uploaded audio is longer than your plan allows (${maxSec}s). The server trimmed it.`
        );
      }
      setUserData((prev) => ({
        ...prev,
        audioUrl: j.audioUrl,
      }));
      setLoading(false);
      toast.success("Audio uploaded and processed successfully!");
    } catch (e) {
      console.error(e);
      setLoading(false);
      toast.error("Failed to upload or extract audio");
    }
  };

  const handlePlayToggle = () => {
    if (!userData?.audioUrl) return;

    setIsActive(true);
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        const prog = (audio.currentTime / audio.duration) * 100;
        setProgress(prog);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      audio.currentTime = 0;
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [userData?.audioUrl]);

  // Get audio duration when metadata loads
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (
        audio.duration &&
        !isNaN(audio.duration) &&
        isFinite(audio.duration)
      ) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    // If metadata already loaded
    if (audio.readyState >= 1) {
      handleLoadedMetadata();
    }

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [userData?.audioUrl]);

  const addUserWidget = async (payload) => {
    try {
      setLoading(true);
      const response = await axiosUserInstance.post(`/widget/add`, payload);
      if (response?.status === 201 || response?.status === 200) {
        const data = await response.data?.data;
        setOpenWidgetModal(false);
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
        }));
        toast.success("Widget added successfully.");
      } else {
        toast.error(
          response?.message || "Widget add failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  // Handle event poster initialization
  const handleAddEventPoster = async () => {
    try {
      setLoading(true);
      const response = await axiosUserInstance.post(`/widget/update-poster`, {
        isShowToEditProfile: true,
      });
      if (response?.status === 200) {
        const data = response.data?.data;
        setOpenWidgetModal(false);
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
        }));
        toast.success(
          "Event Poster initialized. You can now upload your poster."
        );
      } else {
        toast.error(response?.message || "Failed to initialize poster.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileDropdownModal
      isOpen={openWidgetModal}
      onClose={() => setOpenWidgetModal(false)}
      title="Widget Menu"
      className="widget-menu-modal"
      userData={userData}
    >
      <div className="mb-4">
        <div className="menu-title">
          <PiMusicNotesBold size={20} />
          <h6 style={{ fontFamily: userData?.pageTheme?.pageFont }}>Audio</h6>
        </div>
        <div className="music-pill-root" role="region" aria-label="Music pill">
          <div className="music-pill">
            <div className="pill-left">
              <div className="pill-visualizer">
                <canvas ref={canvasRef} className="pill-canvas" />
              </div>
            </div>

            <div className="pill-center">
              <div className="pill-meta">
                <div
                  className="pill-title"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  Audio
                </div>
                <SwitchToggle
                  checked={userData?.showAudio}
                  onChange={() =>
                    updateUserData({
                      showAudio: !userData.showAudio,
                    })
                  }
                />
              </div>

              <div className="pill-controls">
                <button
                  className="pill-play"
                  onClick={handlePlayToggle}
                  aria-pressed={isPlaying}
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  {isPlaying ? "Pause" : "Play"}
                </button>

                <label
                  className="pill-upload"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  Upload
                  <input
                    type="file"
                    accept="video/*,audio/*"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    className="hidden-input"
                  />
                </label>
              </div>

              <div className="pill-progress-wrap">
                <div className="pill-progress-bar" aria-hidden="true">
                  <div
                    className="pill-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div
                  className="pill-duration"
                  style={{ fontFamily: userData?.pageTheme?.pageFont }}
                >
                  {Math.round(duration)}s
                </div>
              </div>

              <div
                className="pill-hint"
                style={{ fontFamily: userData?.pageTheme?.pageFont }}
              >
                Upload a saved video or song as audio
              </div>
            </div>

            <audio
              ref={audioRef}
              src={userData?.audioUrl || ""}
              type="audio/mpeg"
            />
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="menu-title">
          <MdLabelOutline size={20} />
          <h6 style={{ fontFamily: userData?.pageTheme?.pageFont }}>Label</h6>
        </div>
        <div className="widget-menu-list-wrap d-flex align-items-center flex-wrap gap-3">
          <div
            className="widget-menu-list-item"
            onClick={() => {
              setOpenWidgetModal(false);
              openAddSocial();
            }}
          >
            <FaLink size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>
              Social Link
            </p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() => {
              addUserWidget({
                type: "albums",
                albumIds: [],
              });
            }}
          >
            <FaImages size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>Albums</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "title",
                text: "Enter your title here",
              })
            }
          >
            <RxText size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>title</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "description",
                text: "Enter your description here",
              })
            }
          >
            <IoText size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>dscrp</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "note",
                text: "Enter your note here",
              })
            }
          >
            <PiNoteBold size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>note</p>
          </div>
          <div className="widget-menu-list-item">
            <div>
              <FaPlus size={14} /> <CgStack size={18} />
            </div>
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>tabs</p>
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="menu-title">
          <FiImage size={20} />
          <h6 style={{ fontFamily: userData?.pageTheme?.pageFont }}>Media</h6>
        </div>
        <div className="widget-menu-list-wrap d-flex align-items-center flex-wrap gap-3">
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "youtube",
                content: "https://youtu.be/",
              })
            }
          >
            <img src={YouTube} alt="Youtube Icon" width={24} height={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>YT</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() => {
              setAlbumEmbedFormData({ ratio: "1:1" });
              setOpenWidgetModal(false);
              setShowAlbumEmbedModal(true);
            }}
          >
            <div className="link-icon">
              <FaLink />
            </div>
            <img src={ImagePhoto} alt="Image Icon" width={24} height={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>1:1</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() => {
              setAlbumEmbedFormData({ ratio: "16:9" });
              setOpenWidgetModal(false);
              setShowAlbumEmbedModal(true);
            }}
          >
            <div className="link-icon">
              <FaLink />
            </div>
            <img src={ImagePhoto} alt="Image Icon" width={24} height={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>16:9</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() => {
              setAlbumEmbedFormData({ ratio: "32:9" });
              setOpenWidgetModal(false);
              setShowAlbumEmbedModal(true);
            }}
          >
            <div className="link-icon">
              <FaLink />
            </div>
            <img src={ImagePhoto} alt="Image Icon" width={24} height={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>32:9</p>
          </div>
          <div className="widget-menu-list-item">
            <IoGridSharp size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>Album</p>
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="menu-title">
          <LuMapPin size={20} />
          <h6 style={{ fontFamily: userData?.pageTheme?.pageFont }}>Maps</h6>
        </div>
        <div className="widget-menu-list-wrap d-flex align-items-center flex-wrap gap-3">
          <div
            className="widget-menu-list-item"
            onClick={() => {
              if (userData?.planType?.toLowerCase() === "free") {
                toast.info(
                  "Maps is only available for Monthly and Annual Plan users. Please upgrade your plan to showcase this feature."
                );
                return;
              }
              addUserWidget({
                type: "mapDetails",
                lat: 1.3039,
                lon: 103.832,
                label: "Your location",
                isShow: false,
                isShowToEditProfile: false,
              });
            }}
          >
            <div className="premium-icon">
              <GrDiamond />
            </div>
            <img src={MapMarkerIcon} alt="Maps Icon" width={24} height={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>Maps</p>
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="menu-title">
          <LuUserRoundMinus size={20} />
          <h6 style={{ fontFamily: userData?.pageTheme?.pageFont }}>
            Info Collect
          </h6>
        </div>
        <div className="widget-menu-list-wrap d-flex align-items-center flex-wrap gap-3">
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "email",
                text: "your@email.com",
              })
            }
          >
            <img src={Gmail} alt="Gmail Icon" width={24} height={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>email</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "number",
                text: "+65 1234567890",
              })
            }
          >
            <BsTelephone size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>num</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "card",
                email: "",
                number: "+65 1234567890",
                website: "",
              })
            }
          >
            <AiOutlineIdcard size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>card</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() => {
              toast.info("This feature is coming soon");
              // addUserWidget({
              //   isAddAnonymous: true,
              // })
            }}
          >
            <IoChatbubblesOutline size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>anony</p>
          </div>
          <div
            className="widget-menu-list-item"
            onClick={() =>
              addUserWidget({
                type: "form",
                text: "Enter your form here",
              })
            }
          >
            <FaWpforms size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>form</p>
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="menu-title">
          <CiShop size={20} />
          <h6 style={{ fontFamily: userData?.pageTheme?.pageFont }}>Pop-Up</h6>
        </div>
        <div className="widget-menu-list-wrap d-flex align-items-center flex-wrap gap-3">
          <div
            className="widget-menu-list-item premium-option"
            onClick={() => {
              toast.info("This feature is coming soon");
              // addUserWidget({
              //   isAddNumber: true,
              // })
            }}
          >
            <div className="premium-icon">
              <GrDiamond />
            </div>
            <RxCountdownTimer size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>countd</p>
          </div>
          <div
            className="widget-menu-list-item premium-option"
            onClick={() => {
              if (userData?.planType?.toLowerCase() !== "yearly") {
                toast.info(
                  "Poster is only available for Annual Plan users. Please upgrade your plan to showcase this feature."
                );
                return;
              }
              if (
                userData?.eventPoster &&
                Object.keys(userData?.eventPoster)?.length > 0 &&
                userData?.eventPoster?.url
              )
                return;
              handleAddEventPoster();
            }}
          >
            <div className="premium-icon">
              <GrDiamond />
            </div>
            <BsFilePost size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>poster</p>
          </div>
          <div
            className="widget-menu-list-item premium-option"
            onClick={() => {
              if (userData?.planType?.toLowerCase() !== "yearly") {
                toast.info(
                  "Queue is only available for Annual Plan users. Please upgrade your plan to showcase this feature."
                );
                return;
              }
              setOpenWidgetModal(false);
              setShowCountdownModal(true);
            }}
          >
            <div className="premium-icon">
              <GrDiamond />
            </div>
            <FaUsersGear size={24} />
            <p style={{ fontFamily: userData?.pageTheme?.pageFont }}>queue</p>
          </div>
        </div>
      </div>
    </MobileDropdownModal>
  );
};

export default WidgetMenu;
